import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search } from "lucide-react";
import { inr } from "@/lib/api";
import { exportCSV } from "@/lib/exports";
import { ExportBtn } from "@/components/dashboard/ExportBtn";

const STATUS_STYLES = {
  "pending docs": "bg-rose-500/15 text-rose-400 border-rose-500/40",
  "pending": "bg-rose-500/15 text-rose-400 border-rose-500/40",
  "tax paid": "bg-amber-500/15 text-amber-400 border-amber-500/40",
  "registration received": "bg-blue-500/15 text-blue-400 border-blue-500/40",
  "registered": "bg-emerald-500/15 text-emerald-400 border-emerald-500/40",
  "rc dispatched": "bg-emerald-500/15 text-emerald-400 border-emerald-500/40",
};

export const RtoTracker = ({ rto }) => {
  const [filter, setFilter] = useState("All");
  const [q, setQ] = useState("");
  const statuses = useMemo(() => ["All", ...new Set((rto || []).map((r) => r.status))], [rto]);
  const rows = useMemo(() => (rto || []).filter((r) =>
    (filter === "All" || r.status === filter) &&
    (!q || `${r.chassis_no} ${r.customer} ${r.model_name} ${r.work_type}`.toLowerCase().includes(q.toLowerCase()))
  ), [rto, filter, q]);

  if (!rto) return <div className="h-96 bg-[#1E293B] border border-[#334155] rounded-lg animate-pulse" />;

  const isPending = (r) => /pending/i.test(r.status || "");
  const totalPending = rto.filter(isPending).length;
  const pending30 = rto.filter((r) => isPending(r) && (r.age_days || 0) > 30).length;
  const subPending = rto.filter((r) => isPending(r) && /^asc/i.test(r.customer || "")).length;
  const doneCount = rto.length - totalPending;
  const feesSum = rows.reduce((t, r) => t + (r.amount || 0), 0);

  return (
    <div data-testid="rto-tracker" className="bg-[#1E293B] border border-[#334155] rounded-lg p-5">
      <div data-testid="rto-summary" className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {[["rto-sum-pending", totalPending, "Total Pending", "text-rose-400"],
          ["rto-sum-pending30", pending30, "Pending 30+ days", "text-amber-400"],
          ["rto-sum-subdealer", subPending, "Subdealer (ASC) Pending", "text-orange-400"],
          ["rto-sum-done", doneCount, "Done / Other", "text-emerald-400"]].map(([tid, val, label, color]) => (
          <div key={tid} data-testid={tid} className="bg-[#111827] border border-[#334155] rounded-xl px-3 py-3 text-center">
            <p className={`text-2xl font-extrabold font-mono ${color}`} style={{ fontFamily: "Outfit, sans-serif" }}>{val.toLocaleString("en-IN")}</p>
            <p className="text-[9px] uppercase tracking-widest text-slate-400 font-bold mt-0.5">{label}</p>
          </div>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-1" style={{ fontFamily: "Outfit, sans-serif" }}>RTO Work Tracker <span className="text-sm text-slate-400 font-normal">({rows.length} jobs)</span>
          <ExportBtn onCSV={() => exportCSV(rows, "rto-tracker")} />
        </h3>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />
          <Input data-testid="rto-search-input" placeholder="Search chassis / customer…" value={q} onChange={(e) => setQ(e.target.value)}
            className="pl-8 h-9 w-full sm:w-64 bg-[#0B0F17] border-[#334155] text-slate-100 placeholder:text-slate-500" />
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {statuses.map((s) => (
          <button key={s} data-testid={`rto-filter-${s.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors duration-150 active:scale-95 ${filter === s ? "bg-purple-600 border-purple-600 text-white" : "border-[#334155] text-slate-400 hover:text-white hover:border-slate-500"}`}>
            {s}
          </button>
        ))}
      </div>
      <div className="overflow-auto max-h-[560px]">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="sticky top-0 bg-[#1E293B]">
            <tr className="text-left text-xs uppercase tracking-widest text-slate-400 border-b border-[#334155]">
              <th className="pb-2 pr-3">Date</th><th className="pb-2 pr-3">Customer / Party</th><th className="pb-2 pr-3">Chassis</th>
              <th className="pb-2 pr-3">Reg No</th><th className="pb-2 pr-3">Work</th><th className="pb-2 pr-3 text-right">Age</th><th className="pb-2 pr-3 text-right">Fees+Tax</th><th className="pb-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.chassis_no}-${i}`} data-testid={`rto-row-${i}`} className="border-b border-slate-800/60 hover:bg-slate-800/40 transition-colors">
                <td className="py-2.5 pr-3 font-mono text-xs text-slate-400">{r.date || "—"}</td>
                <td className="py-2.5 pr-3 text-slate-200">{r.customer || "—"}</td>
                <td className="py-2.5 pr-3 font-mono text-xs text-slate-300">{r.chassis_no || "—"}</td>
                <td className="py-2.5 pr-3 font-mono text-xs text-cyan-400">{r.registration_no || "—"}</td>
                <td className="py-2.5 pr-3 text-slate-300">{r.work_type}</td>
                <td className={`py-2.5 pr-3 text-right font-mono ${r.age_days != null ? (r.age_days > 60 ? "text-rose-400" : r.age_days > 30 ? "text-amber-400" : "text-slate-300") : "text-slate-600"}`}>
                  {r.age_days != null ? `${r.age_days}d` : "—"}
                </td>
                <td className="py-2.5 pr-3 text-right font-mono">{r.amount ? inr(r.amount) : "—"}</td>
                <td className="py-2.5">
                  <Badge variant="outline" className={`text-[10px] ${STATUS_STYLES[r.status?.toLowerCase()] || "bg-slate-500/15 text-slate-400 border-slate-500/40"}`}>
                    {r.status}
                  </Badge>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-slate-500">No RTO jobs match.</td></tr>}
          </tbody>
          <tfoot>
            <tr data-testid="rto-total-row" className="border-t-2 border-[#334155] font-bold">
              <td colSpan={5} className="py-2.5 pr-3 text-slate-100">Total shown: {rows.length}</td>
              <td></td>
              <td className="py-2.5 pr-3 text-right font-mono text-slate-100">{feesSum ? inr(feesSum) : "—"}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};
